import React, { ReactElement } from 'react'
import './App.scss'
import { Authenticator } from '@aws-amplify/ui-react'
import type {} from '@aws-amplify/ui-react'
import '@aws-amplify/ui-react/styles.css'
import { fetchUserAttributes } from '@aws-amplify/auth'
import applyFetchInterceptor from './api/applyFetchInterceptor'
import fetchUserCredentials from './api/fetchUserCredentials'
import Footer from './components/Footer'
import Header from './components/Header'
import LabelForm from './components/form/LabelForm'
import useLabels from './components/form/useLabels'
import useClip from './components/video_player/useClip'
import VideoPlayer from './components/video_player/VideoPlayer'
import AnimatedGraph from './components/wearable_data/animatedGraph'
import { fetchSensorData } from './api/fetchChartData'
import PreviousClipThumbnail from './components/dashboard/PreviousClipThumbnail'

export type LabelConfig = {
  labels: Record<string, string[]>
  labelNames: string[]
  issueNames: string[]
  totalLabelNames: string[]
  labelFormModel: Record<string, boolean>
  labelInfo: Record<string, string>
}

export type ErrorMessage = {
  error: string
}

export type Message = {
  message: string
}

export type Authentication = {
  loggedIn: boolean
  accessToken?: string
  refreshToken?: string
}

export function App(): ReactElement {
  const [loginStatus, setLoginStatus] = React.useState<boolean>(false)
  applyFetchInterceptor(setLoginStatus)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const { labelConfig } = useLabels(loginStatus)
  const { currentClip, setCurrentClip, setPreviousClip } = useClip(loginStatus)
  const [accelData, setAccelData] = React.useState<{ x: number; y: number }[]>([])
  const [emgData, setEmgData] = React.useState<{ x: number; y: number }[]>([])
  const [biozData, setBiozData] = React.useState<{ x: number; y: number }[]>([])
  const [ppgData, setPpgData] = React.useState<{ x: number; y: number }[]>([])
  const [spo2Data, setSpo2Data] = React.useState<{ x: number; y: number }[]>([])
  const [tempData, setTempData] = React.useState<{ x: number; y: number }[]>([])
  const [currentTime, setCurrentTime] = React.useState<number>(0) // State to track video current time
  const [roleTag, setRoleTag] = React.useState<string | null>(null)

  const fetchAttributesAndLogin = async (): Promise<void> => {
    try {
      const attributes = await fetchUserAttributes()
      const role = attributes['custom:role']
      const { email } = attributes
      setRoleTag(role || null)
      console.log('user role', roleTag)
      const defaultCredentials = {
        username: email || 'z', // Use the fetched email
        password: 'default_password',
        role,
      }

      const response = await fetchUserCredentials('POST', false, defaultCredentials)

      if ('error' in response) {
        setLoginStatus(false)
      } else {
        if (response.accessToken) {
          localStorage.setItem('accessToken', response.accessToken)
          console.log('generating')
        }
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
        setLoginStatus(response.loggedIn || false)
      }
    } catch (err) {
      console.error('Error fetching user attributes or credentials:', err)
      setLoginStatus(false)
    }
  }

  React.useEffect(() => {
    fetchUserCredentials('GET', false)
      .then((response: Authentication | ErrorMessage) => {
        if (!('error' in response)) setLoginStatus(response.loggedIn || false)
      })
      .catch(err => {
        throw new Error(err.message)
      })
  }, [])

  React.useEffect(() => {
    const fetchData = async (): Promise<void> => {
      if (loginStatus && currentClip) {
        console.log('fetching sensor data')
        fetchSensorData('accelerometer', currentClip)
          .then(data => setAccelData(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setAccelData([])
          })

        fetchSensorData('emg', currentClip)
          .then(data => setEmgData(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setEmgData([])
          })

        fetchSensorData('temperature', currentClip)
          .then(data => setTempData(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setTempData([])
          })

        fetchSensorData('ppg', currentClip)
          .then(data => setPpgData(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setPpgData([])
          })

        fetchSensorData('spo2', currentClip)
          .then(data => setSpo2Data(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setSpo2Data([])
          })

        fetchSensorData('bioz', currentClip)
          .then(data => setBiozData(data))
          .catch(error => {
            console.error('Error fetching accelerometer data:', error)
            setBiozData([])
          })
      }
    }
    fetchData()
  }, [loginStatus, currentClip])

  return (
    <div className="App">
      <Authenticator loginMechanisms={['email']} hideSignUp>
        <Header loginStatus={loginStatus} setLoginStatus={setLoginStatus} />
        {loginStatus ? (
          <div className="row">
            <div className="mainColumn">
              <VideoPlayer currentClip={currentClip} videoRef={videoRef} onTimeUpdate={setCurrentTime} />
            </div>
            <div className="sideColumn">
              <AnimatedGraph
                data={tempData}
                title="Temperature"
                yAxisMin={25}
                yAxisMax={39}
                currentTime={currentTime}
              />
              <AnimatedGraph data={ppgData} title="PPG" yAxisMin={10000} yAxisMax={200000} currentTime={currentTime} />
              <AnimatedGraph data={spo2Data} title="SPO2" yAxisMin={70} yAxisMax={110} currentTime={currentTime} />
              <div className="buttons">
                <PreviousClipThumbnail currentClip={currentClip} setCurrentClip={setCurrentClip} />
              </div>
            </div>
            <div className="sideColumn">
              <AnimatedGraph
                data={accelData}
                title="Accelerometer"
                yAxisMin={-1000}
                yAxisMax={2000}
                currentTime={currentTime}
              />
              <AnimatedGraph data={biozData} title="BioZ" yAxisMin={24} yAxisMax={66} currentTime={currentTime} />
              <AnimatedGraph data={emgData} title="EMG" yAxisMin={0} yAxisMax={1500} currentTime={currentTime} />
            </div>
            <div className="label-section">
              <LabelForm
                labelConfig={labelConfig}
                currentClip={currentClip}
                loginStatus={loginStatus}
                setCurrentClip={setCurrentClip}
                setPreviousClip={setPreviousClip}
              />
            </div>
          </div>
        ) : (
          <div className="loginRegister">
            <div className="welcome-message">
              <h1>Thank You, Doctors!</h1>
              <br />
              <p>
                We deeply appreciate the time and expertise you contribute to annotating data. Your efforts are crucial
                in training our machine learning models to treat neonatal abstinence syndrome (NAS) in babies.
              </p>
              <br />

              <p>
                Please ensure to read through the annotation procedure carefully before beginning. Your precision helps
                us move forward.
              </p>
              <br />
              <p>
                If you are not familiar with the annotation procedure or need further assistance, please contact{' '}
                <a href="mailto:suma.katabattuni@rekovar.com">Suma Katabattuni</a>.
              </p>
              <br />
              <button type="submit" onClick={fetchAttributesAndLogin} className="get-started-btn">
                Get Started
              </button>
            </div>
          </div>
        )}
        {!loginStatus && <Footer />}
      </Authenticator>
    </div>
  )
}

export default App
